/**
 * QuotesWebSocketHandler — live quotes via the **trading** WebSocket only (`/ws/trading`, channel: quotes).
 * The charts socket (`/ws/charts`) is for bars / marketAggregates only — DevTools will not show quote subs there.
 * Auto-subscribes on load and re-subscribes when the symbol set changes.
 */
import { useContext, useEffect, useMemo, useRef } from 'react';
import { TradingContext } from '../contexts/TradingContext';
import { AppStateContext } from '../contexts/AppStateContext';
import { StreamsContext } from '../contexts/StreamsContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { BannerContext } from '../contexts/BannerContext';
import { brokerQuoteSnapshotsUrl } from '../services/brokerApi';
import { useErrorReporting } from '../hooks/useErrorReporting';
import { AuthContext } from '../contexts/AuthContext';
import {
  supportsMarketData,
  resolveMarketDataConnection,
  getMarketDataRouting,
  getUnsupportedAssetClassBlock,
  watchlistQuotesEnabled,
} from '../utils/brokerConnection';
import {
  onStreamRecovered,
  reportStreamError,
  shouldDeferStreamErrorBanner,
} from '../utils/streamErrorBanner';
import { MARKET_INDEX_SYMBOLS } from '../constants/marketIndices';
import { mergeQuotePatches } from '../utils/quoteBatch';

const QUOTES_DEBOUNCE_MS = 500;

/** REST quote snapshots are TradeStation Direct only; CT uses the stream. */
function shouldFetchRestQuoteSnapshots(user, selectedAccount) {
  const md = resolveMarketDataConnection(user, selectedAccount);
  return md?.adapter_type === 'tradestation_direct';
}

export default function QuotesWebSocketHandler() {
  const { user } = useContext(AuthContext);
  const { positions, selectedAccount } = useContext(TradingContext);
  const { selectedTicker, watchlistTickers } = useContext(AppStateContext);
  const { setQuotesBySymbol, setQuoteStreamHealthy } = useContext(StreamsContext);
  const { tradingReady, sendTrading, addTradingListener } = useWebSocket();
  const { showMessage } = useContext(BannerContext);
  const { reportError } = useErrorReporting();
  const subscribedSymbolsRef = useRef('');
  const snapshotAbortRef = useRef(null);
  const quoteHealthyRef = useRef(true);
  const tradingReadyRef = useRef(tradingReady);

  const mdRouting = getMarketDataRouting(user, selectedAccount);
  const selectedAccountId = mdRouting.accountId;
  const selectedConnectionId = mdRouting.connectionId;
  const selectedAdapterType = mdRouting.adapterType;
  const fetchRestSnapshots = shouldFetchRestQuoteSnapshots(user, selectedAccount);

  useEffect(() => {
    tradingReadyRef.current = tradingReady;
    if (!tradingReady) quoteHealthyRef.current = false;
  }, [tradingReady]);

  const includeWatchlistQuotes = watchlistQuotesEnabled(user, selectedAccount);

  const symbolsList = useMemo(() => {
    const normalize = (s) => (s == null || s === '' ? null : String(s).trim().toUpperCase() || null);
    const set = new Set();
    if (normalize(selectedTicker)) set.add(normalize(selectedTicker));
    (Array.isArray(positions) ? positions : []).forEach((p) => {
      const s = normalize(p?.Symbol);
      if (s) set.add(s);
    });
    // Tastytrade: skip watchlist symbols. Their day $/% quotes need 2 daily bars
    // on the shared Connect Trade socket and currently stall chart streams.
    if (includeWatchlistQuotes) {
      (Array.isArray(watchlistTickers) ? watchlistTickers : []).forEach((t) => {
        const s = normalize(t?.ticker);
        if (s) set.add(s);
      });
    }
    MARKET_INDEX_SYMBOLS.forEach((s) => {
      const n = normalize(s);
      if (n) set.add(n);
    });
    const sorted = Array.from(set)
      .filter((symbol) => !getUnsupportedAssetClassBlock(user, selectedAccount, symbol))
      .sort();
    // Before AppState finishes hydrating, selectedTicker can be null and the set empty — still subscribe so the stream starts.
    if (sorted.length === 0) {
      return ['SPY'];
    }
    return sorted;
  }, [selectedTicker, positions, watchlistTickers, includeWatchlistQuotes, user, selectedAccount]);

  const symbolsCsv = symbolsList.join(',');

  const pendingQuotesRef = useRef(null);
  const quoteRafRef = useRef(0);
  const flushQueuedQuotes = () => {
    quoteRafRef.current = 0;
    const pending = pendingQuotesRef.current;
    pendingQuotesRef.current = null;
    if (!pending) return;
    setQuotesBySymbol((prev) => mergeQuotePatches(prev, pending));
  };
  const queueQuotePatches = (patches) => {
    if (!patches) return;
    if (!pendingQuotesRef.current) pendingQuotesRef.current = {};
    const bucket = pendingQuotesRef.current;
    for (const symbol of Object.keys(patches)) {
      const data = patches[symbol];
      if (!symbol || !data) continue;
      bucket[symbol] = { ...(bucket[symbol] || {}), ...data };
    }
    if (quoteRafRef.current) return;
    quoteRafRef.current = requestAnimationFrame(flushQueuedQuotes);
  };

  useEffect(() => () => {
    if (quoteRafRef.current) cancelAnimationFrame(quoteRafRef.current);
  }, []);

  const handlersRef = useRef({});
  handlersRef.current = {
    queueQuotePatches,
    setQuoteStreamHealthy,
    showMessage,
    reportError,
    token: user?.token,
    symbolsCsv,
    selectedAccountId,
    selectedConnectionId,
    selectedAdapterType,
    fetchRestSnapshots,
  };

  // Listen for quote messages on the trading WebSocket
  useEffect(() => {
    if (!tradingReady) return;

    const remove = addTradingListener((msg) => {
      if (!msg || msg.channel !== 'quotes') return;
      const data = msg.data;
      if (!data) return;

      const H = handlersRef.current;

      if (data.Error) {
        quoteHealthyRef.current = false;
        H.setQuoteStreamHealthy(false);
        const message = data.Message || 'Quote stream error';
        reportStreamError({
          streamKey: 'quotes',
          message,
          showMessage: H.showMessage,
          defer: shouldDeferStreamErrorBanner(data, tradingReadyRef.current),
          isHealthy: () => quoteHealthyRef.current,
        });
        return;
      }
      if (data.Heartbeat) {
        quoteHealthyRef.current = true;
        onStreamRecovered('quotes');
        H.setQuoteStreamHealthy(true);
        return;
      }
      if (data.LateJoin) {
        quoteHealthyRef.current = true;
        onStreamRecovered('quotes');
        H.setQuoteStreamHealthy(true);
        // Only Direct uses REST snapshots; never hit a sibling CT broker (e.g. Webull).
        if (!H.token || !H.fetchRestSnapshots) return;
        fetch(
          brokerQuoteSnapshotsUrl(
            H.symbolsCsv,
            H.selectedAccountId,
            H.selectedConnectionId,
            H.selectedAdapterType
          ),
          { headers: { Authorization: `Bearer ${H.token}` } }
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((resp) => {
            if (resp?.Quotes?.length) {
              const map = {};
              resp.Quotes.forEach((q) => {
                if (q.Symbol) map[q.Symbol] = { ...q };
              });
              H.queueQuotePatches(map);
            }
          })
          .catch((e) =>
            H.reportError?.(e, { component: 'QuotesWebSocketHandler', action: 'lateJoinFetch' })
          );
        return;
      }
      if (data.Symbol) {
        quoteHealthyRef.current = true;
        onStreamRecovered('quotes');
        H.queueQuotePatches({ [data.Symbol]: data });
        H.setQuoteStreamHealthy(true);
        return;
      }
      if (Array.isArray(data?.Quotes) && data.Quotes.length) {
        quoteHealthyRef.current = true;
        onStreamRecovered('quotes');
        const patches = {};
        for (const q of data.Quotes) {
          if (!q?.Symbol) continue;
          patches[q.Symbol] = { ...(patches[q.Symbol] || {}), ...q };
        }
        H.queueQuotePatches(patches);
        H.setQuoteStreamHealthy(true);
      }
    });

    return remove;
  }, [tradingReady, addTradingListener]);

  // Subscribe / re-subscribe when symbols change or trading WS becomes ready
  useEffect(() => {
    const token = user?.token;
    const canRun = !!token && tradingReady && supportsMarketData(user, selectedAccount);

    if (!canRun || !symbolsCsv) {
      return () => {};
    }

    const timer = setTimeout(() => {
      // Make-before-break: server replaces subscription on new subscribe; no prior unsubscribe.
      const subMsg = {
        type: 'subscribe',
        channel: 'quotes',
        symbols: symbolsCsv,
      };
      if (selectedAccountId) subMsg.accountId = selectedAccountId;
      if (selectedConnectionId) subMsg.connectionId = selectedConnectionId;
      if (selectedAdapterType) subMsg.adapterType = selectedAdapterType;
      sendTrading(subMsg);
      subscribedSymbolsRef.current = symbolsCsv;

      // TradeStation Direct: snapshot for immediate display while stream warms up.
      // Connect Trade: quotes arrive via stream only (no REST snapshot).
      // Pass the effective MD connection so a non-MD selected account still routes correctly.
      if (fetchRestSnapshots) {
        try { snapshotAbortRef.current?.abort(); } catch (_) {}
        const ctrl = new AbortController();
        snapshotAbortRef.current = ctrl;

        fetch(
          brokerQuoteSnapshotsUrl(
            symbolsCsv,
            selectedAccountId,
            selectedConnectionId,
            selectedAdapterType
          ),
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: ctrl.signal,
          }
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((resp) => {
            if (resp?.Quotes?.length) {
              const map = {};
              resp.Quotes.forEach((q) => {
                if (q.Symbol) map[q.Symbol] = { ...q };
              });
              handlersRef.current.queueQuotePatches(map);
              handlersRef.current.setQuoteStreamHealthy(true);
            }
          })
          .catch((e) => {
            if (e?.name === 'AbortError') return;
            handlersRef.current.reportError?.(e, {
              component: 'QuotesWebSocketHandler',
              action: 'initialQuoteFetch',
            });
          });
      }
    }, QUOTES_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      try { snapshotAbortRef.current?.abort(); } catch (_) {}
      snapshotAbortRef.current = null;
    };
  }, [
    symbolsCsv,
    user?.token,
    user,
    selectedAccount,
    selectedAccountId,
    selectedConnectionId,
    selectedAdapterType,
    fetchRestSnapshots,
    tradingReady,
    sendTrading,
    setQuoteStreamHealthy,
  ]);

  // Unsubscribe on unmount
  useEffect(() => {
    return () => {
      if (subscribedSymbolsRef.current) {
        const unsub = {
          type: 'unsubscribe',
          channel: 'quotes',
          symbols: subscribedSymbolsRef.current,
        };
        if (selectedAccountId) unsub.accountId = selectedAccountId;
        if (selectedConnectionId) unsub.connectionId = selectedConnectionId;
        if (selectedAdapterType) unsub.adapterType = selectedAdapterType;
        sendTrading(unsub);
        subscribedSymbolsRef.current = '';
      }
    };
  }, [sendTrading, selectedAccountId, selectedConnectionId, selectedAdapterType]);

  return null;
}
